import type {
  ActivityItem,
  ContactChannel,
  CustomerPlan,
  CustomerStatus,
} from "@/api/customers";
import type { StatusTone } from "@/components/shared/status-badge";
import { activityLine as profileActivityLine } from "../profile/display";

/** How each customer status reads, and its tone — the one mapping. */
export const CUSTOMER_STATUS: Record<
  CustomerStatus,
  { label: string; tone: StatusTone }
> = {
  prospect: { label: "Prospect", tone: "info" },
  active: { label: "Active", tone: "success" },
  paused: { label: "Paused", tone: "warning" },
  inactive: { label: "Inactive", tone: "neutral" },
};

export const CHANNEL_LABEL: Record<ContactChannel, string> = {
  whatsapp: "WhatsApp",
  call: "Call",
  ticket: "Ticket",
};

/** "CX-1234" — how staff say a customer number. */
export const customerCode = (id: number): string => `CX-${id}`;

/** A plan's one badge: Active, Ends in N days (≤ 7), Paused or Payment pending. */
export function planStatus(plan: CustomerPlan): { label: string; tone: StatusTone } {
  if (plan.status === "payment_pending")
    return { label: "Payment pending", tone: "warning" };
  if (plan.status === "paused") return { label: "Paused", tone: "warning" };
  if (plan.daysLeft !== null && plan.daysLeft <= 7) {
    return {
      label:
        plan.daysLeft <= 0
          ? "Ends today"
          : `Ends in ${plan.daysLeft} ${plan.daysLeft === 1 ? "day" : "days"}`,
      tone: "warning",
    };
  }
  return { label: "Active", tone: "success" };
}

/** "3h 10m", "2d 4h" from minutes — how late something is. */
export function lateBy(minutes: number): string {
  const total = Math.max(0, Math.round(minutes));
  const days = Math.floor(total / 1440);
  const hours = Math.floor((total % 1440) / 60);
  const mins = total % 60;
  if (days > 0) return hours ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return mins ? `${hours}h ${mins}m` : `${hours}h`;
  return `${mins}m`;
}

export { CALL_STATUS_WORD } from "../profile/display";

/** One line for an activity item, for the Overview's compact list. */
export const activityLine = (item: ActivityItem): string => profileActivityLine(item);
